import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

export const WIDESCREEN_ASPECT_RATIO = 16 / 9; // ~1.7778
export const MIN_WIDESCREEN_RATIO = 1.7;
export const MAX_WIDESCREEN_RATIO = 1.85;

export const PITCH_DECK_DIMENSION_ERROR = 'Please use widescreen slides (16:9)';

/**
 * Checks if the given width and height correspond to standard widescreen (16:9).
 * Allows a small float tolerance (1.70 to 1.85) to accommodate PDF point rounding.
 */
export function isWidescreenRatio(width: number, height: number): boolean {
  if (!width || !height || width <= 0 || height <= 0) return false;
  const ratio = width / height;
  return ratio >= MIN_WIDESCREEN_RATIO && ratio <= MAX_WIDESCREEN_RATIO;
}

export type PitchDeckDimensionResult = {
  isValid: boolean;
  width?: number;
  height?: number;
  ratio?: number;
  error?: string;
};

/**
 * Reads the first page dimensions of a PDF file buffer to verify 16:9 widescreen ratio.
 */
export async function checkPdfIsWidescreen(
  data: ArrayBuffer | Uint8Array
): Promise<PitchDeckDimensionResult> {
  try {
    if (typeof window !== 'undefined' && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
    }

    const uint8 = data instanceof Uint8Array ? data : new Uint8Array(data);
    const loadingTask = pdfjsLib.getDocument({
      data: uint8,
      disableWorker: true,
    });
    const pdf = await loadingTask.promise;
    if (pdf.numPages < 1) {
      return { isValid: false, error: PITCH_DECK_DIMENSION_ERROR };
    }
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 });
    const { width, height } = viewport;
    const isValid = isWidescreenRatio(width, height);

    return {
      isValid,
      width,
      height,
      ratio: height > 0 ? width / height : 0,
      error: isValid ? undefined : PITCH_DECK_DIMENSION_ERROR,
    };
  } catch (err) {
    console.error('Error verifying pitch deck dimensions:', err);
    return {
      isValid: false,
      error: PITCH_DECK_DIMENSION_ERROR,
    };
  }
}
